import React, { FC, useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  delProfileImage,
  fetchProfile,
  updateProfile,
} from '../../../../states/containers/setting/profileSlice'
import {
  required,
  maxLength,
  email,
  password,
} from '../../../../utils/validate'

import { useTranslation } from 'react-i18next'
import { replaceAndStrip } from '../../../../utils/string'
import ProfileImageModal from '../../../components/common/ProfileImageModal'
import { updateCommonPart } from '../../../../states/containers/common/commonPartSlice'
import ConfirmationModal from '../../../components/setting/profile/ConfirmationModal'
import WorkStyleModal from '../../../components/common/WorkStyleModal'
import WorkStyleTip from '../../../components/common/WorkStyleTip'
import { EditProfileRequest } from '../../../../states/types/member'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-common-types'
import LoadingSquare from '../../../components/common/LoadingSquare'
import * as AutoKana from 'vanilla-autokana'

interface ProfileDetail {
  name: string
  kana: string | null
  email: string
  password: string | null
  password_confirm: string | null
  contract: string | null
  place: string | null
}

interface Errors {
  name: string[],
  email: string[],
  password: string[],
  password_confirm: string[],
}

interface ConfirmationModalState {
  display: boolean,
  callback: VoidFunction,
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    profile,
    login,
  } = useSelector((state: RootState) => state)

  const initialInput = {
    name: "",
    kana: null,
    email: "",
    password: null,
    password_confirm: null,
    contract: null,
    place: null,
  }

  const initErrors = {
    name: [],
    email: [],
    password: [],
    password_confirm: [],
  }

  const [profileData, setProfileData] = useState<ProfileDetail>(initialInput)
  const [isSubmit, setSubmit] = useState(true)

  const [errors, setErrors] = useState<Errors>(initErrors)

  const [isDisplayProfileImageModal, setIsDisplayProfileImageModal] = useState(false)
  const [profileImageOrg, setProfileImageOrg] = useState<string | ArrayBuffer | null>(null)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    callback: () => {
      // none
    },
  })

  const [openPassword, setOpenPassword] = useState(false)

  const [isDisplayWorkStyleModal, setIsDisplayWorkStyleModal] = useState(false)

  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const kanaRef = useRef() as React.MutableRefObject<HTMLInputElement>

  const replaceAndStripName = (event: React.FocusEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      name: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        name: [],
      })
    }
  }

  // email
  const replaceAndStripEmail = (event: React.FocusEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      email: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        email: [],
      })
    }
  }

  // password
  const updatePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      password: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        password: [],
      })
    }
  }

  // contract
  const updateContract = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      contract: replaceAndStrip(event.target.value),
    })
  }

  const updateContractTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      contract: event.target.value,
    })
  }

  // place
  const updatePlace = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      place: replaceAndStrip(event.target.value),
    })
  }

  const updatePlaceTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      place: event.target.value,
    })
  }

  const updatePasswordConfirmation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      password_confirm: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        password_confirm: [],
      })
    }
  }

  useEffect(() => {
    if (!commonPart.company?.id) return
    dispatch(fetchProfile())
  }, [])

  useEffect(() => {
    if (!profile) return
    setProfileData({
      ...initialInput,
      name: profile.name,
      kana: profile.kana,
      email: profile.email,
      password: null,
      password_confirm: null,
      contract: profile.contract,
      place: profile.place,
    })
  }, [profile])

  const handleSubmit = () => {
    if (!commonPart.company?.id) return
    const tmpErrors: Errors = initErrors
    let errorFlg = false
    // name
    const nameEmpty = required('name')(profileData.name)
    if (nameEmpty) {
      tmpErrors.name.push(t(nameEmpty, { label: t('model.member.name') }))
      errorFlg = true
    }
    const nameLength = maxLength(30)(profileData.name)
    if (nameLength) {
      tmpErrors.name.push(t(nameLength, { length: 30 }))
      errorFlg = true
    }
    // email
    const emailEmpty = required('email')(profileData.email)
    if (emailEmpty) {
      tmpErrors.email.push(t(emailEmpty, { label: t('model.member.email') }))
      errorFlg = true
    }
    const emailError = email(profileData.email)
    if (emailError) {
      tmpErrors.email.push(t(emailError))
      errorFlg = true
    }
    if (openPassword) {
      // password
      const passwordError = password(profileData?.password || "", { email: profileData.email })
      if (passwordError) {
        tmpErrors.password = passwordError.map((error) => t(error))
        errorFlg = true
      }
      // password_confirmation
      if (profileData.password !== profileData.password_confirm) {
        tmpErrors.password_confirm.push(t('form.error.confirmation_not_match'))
        errorFlg = true
      }
    }
    setErrors({
      name: tmpErrors.name,
      email: tmpErrors.email,
      password: tmpErrors.password,
      password_confirm: tmpErrors.password_confirm,
    })
    if (errorFlg) return

    const params: EditProfileRequest = {}
    if (profileData.name !== profile.name) {
      params.name = profileData.name
    }
    if (kanaRef.current.value !== profile.kana) {
      params.kana = replaceAndStrip(kanaRef.current.value)
    }
    if (profileData.email !== profile.email) {
      params.email = profileData.email
    }
    if (profileData.contract !== profile.contract) {
      params.contract = profileData.contract
    }
    if (profileData.place !== profile.place) {
      params.place = profileData.place
    }
    if (openPassword && profileData.password) {
      params.password = profileData.password
      params.password_confirm = profileData.password_confirm
    }
    if (!Object.keys(params).length) return
    setSubmit(false)
    dispatch(
      updateProfile(
        params,
        () => {
          setSubmit(true)
          alert.success(t('form.alert.profile_updated'))
        },
        (e: { errors: { column_name: string, message: string[] }[] }) => {
          setSubmit(true)
          e.errors.forEach((value) => {
            if (value.column_name === "email" && value.message[0] === "メールアドレスはすでに存在します") {
              setErrors({
                ...errors,
                email: [t('validate.email_duplicate')],
              })
            }
            alert.error(t('validate.server_error', { name: value.column_name }))
          })
        },
      ),
    )
  }

  const selectProfileImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setProfileImageOrg(reader.result)
    })
    reader.readAsDataURL(e.target.files[0])
    setIsDisplayProfileImageModal(true)
  }

  const handleProfileImage = () => {
    inputRef.current.click()
  }

  const closeProfileImageModal = () => {
    setProfileImageOrg(null)
    setIsDisplayProfileImageModal(false)
    inputRef.current.value = ""
  }

  const DeleteProfileImage = () => {
    dispatch(delProfileImage(
      () => {
        alert.success(t('setting.profile.image.delete'))
        dispatch(fetchProfile())
        dispatch(updateCommonPart())
      },
      () => {
        alert.error(t('setting.profile.image.error'))
      },
    ))
  }

  // confirmation modal
  const openConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      callback: () => DeleteProfileImage(),
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      callback: () => {
        // none
      },
    })
  }

  useEffect(() => {
    setTimeout(() => {
      AutoKana.bind('#name', '#kana')
    }, 1000)
  }, [])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 個人設定 - ${t('setting.profile.caption')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        個人設定
        &nbsp;&gt;&nbsp;
        {t('setting.profile.caption')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.profile.caption')}
        </h1>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          {
            profile.isLoading ? (
              <LoadingSquare>
                <>
                  プロフィール設定を読み込んでいます…
                </>
              </LoadingSquare>
            ) : (
              <div className="profile_wrapper">
                {
                  login.function_enabled_state?.ai_work_assessment && (
                    <div className="fixed_work_style_tip">
                      <WorkStyleTip
                        openCallback={() => setIsDisplayWorkStyleModal(true)}
                      />
                    </div>
                  )
                }
                <div className="left_area">
                  <div className="profile_image_wrapper">
                    <div
                      className="profile_image"
                      onClick={() => handleProfileImage()}
                      role="presentation"
                    >
                      {
                        !profile.isLoading && (
                          profile.profile_image_url ? (
                            <img alt="" src={profile.profile_image_url} />
                          ) : (
                            <img
                              alt=""
                              src={`/static/images/profile/${(commonPart.current_member?.id || 0) % 12}.jpeg`}
                            />
                          )
                        )
                      }
                      <div className="plus_icon">
                        +
                      </div>
                    </div>
                    <div className="hidden">
                      <input type="file" accept="image/*" onChange={(e) => selectProfileImage(e)} ref={inputRef} className="hidden" />
                    </div>
                    {
                      profile.profile_image_url && (
                        <div
                          role="none"
                          onClick={() => openConfirmationModal()}
                          className="profile_image_delete"
                        >
                          {t('setting.profile.button.delete')}
                        </div>
                      )
                    }
                  </div>
                </div>
                <div className="right_area">
                  <fieldset>
                    <legend>
                      基本情報
                    </legend>
                    <div className="item">
                      <div className="title">
                        名前
                      </div>
                      <div className="detail row_1">
                        <input
                          id="name"
                          type="text"
                          defaultValue={profileData?.name || ""}
                          placeholder={t('model.member.name')}
                          onChange={(e) => {
                            setProfileData({
                              ...profileData,
                              name: e.target.value,
                            })
                          }}
                          onBlur={(event) => replaceAndStripName(event)}
                        />
                      </div>
                      {
                        errors.name && errors.name.map((error) => (
                          <div className="error" key={error}>
                            {error}
                          </div>
                        ))
                      }
                    </div>
                    <div className="item">
                      <div className="title">
                        かな
                      </div>
                      <div className="detail row_1">
                        <input
                          ref={kanaRef}
                          id="kana"
                          type="text"
                          placeholder="よみがな"
                          defaultValue={profileData.kana || ""}
                        />
                      </div>
                    </div>
                    <div className="item">
                      <div className="title">
                        メールアドレス
                      </div>
                      <div className="detail row_1">
                        <input
                          type="text"
                          defaultValue={profileData?.email || ""}
                          placeholder={t('model.member.email')}
                          onChange={(e) => {
                            setProfileData({
                              ...profileData,
                              email: e.target.value,
                            })
                          }}
                          onBlur={(event) => replaceAndStripEmail(event)}
                        />
                      </div>
                      {
                        errors.email && errors.email.map((error) => (
                          <div className="error" key={error}>
                            {error}
                          </div>
                        ))
                      }
                    </div>
                    <div className="item">
                      <div className="title">
                        契約形態
                      </div>
                      <div className="detail row_1">
                        <input
                          type="text"
                          placeholder={t('model.member_detail.contract')}
                          onChange={(event) => updateContractTmp(event)}
                          onBlur={(event) => updateContract(event)}
                          value={profileData.contract || ""}
                        />
                      </div>
                    </div>
                    <div className="item">
                      <div className="title">
                        勤務地
                      </div>
                      <div className="detail row_1">
                        <input
                          type="text"
                          placeholder={t('model.member_detail.place')}
                          onChange={(event) => updatePlaceTmp(event)}
                          onBlur={(event) => updatePlace(event)}
                          value={profileData.place || ""}
                        />
                      </div>
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend>
                      パスワード
                    </legend>
                    <div className="item">
                      <div className="title">
                        <div style={{ display: 'flex', gap: "8px", alignItems: "center" }}>
                          パスワード
                          <div
                            onClick={() => setOpenPassword(!openPassword)}
                            role="none"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={['far', `${!openPassword ? 'check-square' : 'square'}` as IconName]}
                              className={`icon ${!openPassword ? "checked" : "no_check"}`}
                              style={{
                                fontSize: "18px",
                                color: !openPassword ? "#08984C" : "#666666",
                              }}
                            />
                            <div>
                              <div className="check_box_title">
                                パスワードを変更しない
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {
                        openPassword && (
                          <>
                            <div className="detail row_1">
                              <input
                                type="text"
                                defaultValue={profileData?.password || ""}
                                placeholder={t('model.member.password')}
                                onChange={(event) => updatePassword(event)}
                              />
                            </div>
                            {
                              errors.password && errors.password.map((error) => (
                                <div className="error" key={error}>
                                  {error}
                                </div>
                              ))
                            }
                          </>
                        )
                      }
                    </div>
                    {
                      openPassword && (
                        <>
                          <div className="item">
                            <div className="title">
                              パスワード（確認用）
                            </div>
                            <div className="detail row_1">
                              <input
                                type="text"
                                defaultValue={profileData?.password_confirm || ""}
                                placeholder={t('model.member.password_confirmation')}
                                onChange={(event) => updatePasswordConfirmation(event)}
                              />
                            </div>
                            {
                              errors.password_confirm && errors.password_confirm.map((error) => (
                                <div className="error" key={error}>
                                  {error}
                                </div>
                              ))
                            }
                          </div>
                        </>
                      )
                    }
                  </fieldset>
                  <div className="button_area">
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      disabled={!isSubmit}
                    >
                      更新
                    </button>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </section>
      <ProfileImageModal
        isOpen={isDisplayProfileImageModal}
        closeCallback={closeProfileImageModal}
        profileImageProp={profileImageOrg}
      />
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
      {/* 働き方モーダル */}
      <WorkStyleModal
        display={isDisplayWorkStyleModal}
        type="profile"
        closeCallback={() => setIsDisplayWorkStyleModal(false)}
      />
    </section>
  )
}

export default Index
